import { KpiIcon } from '@salescore/client-common'
import { getRecordNodeParents, type ViewQueryRecordNode, type ViewQueryRecordNodeWithParent } from '@salescore/core'
import { Tooltip } from 'antd'
import type { Array } from 'effect'
import { type ReactElement, useMemo } from 'react'

import type { RSheetColumn, RSheetRecordNode } from '../../../../../domain/column'
import type { RSheetsCursor } from '../../../../../domain/cursor/cursorTypes'
import { getKpiHelpText } from '../../../../../hooks/usePivotColumns'
import type { RSheetContext } from '../../../../../recoil/context'
import { useCursorModel } from '../../../../../recoil/cursor'
import { useRecordsImpl } from '../../../../../recoil/records'
import { buildKpisMapper } from '../../../../recoil/selectors/buildKpisMapper'

export const RowKpiCell = ({
  value,
  recordNode,
  column,
  context,
  columns,
}: {
  value: unknown
  recordNode: ViewQueryRecordNodeWithParent
  column: RSheetColumn<RSheetRecordNode>
  context: RSheetContext
  columns: Array<RSheetColumn<RSheetRecordNode>>
}): ReactElement => {
  const kpisMapper = useMemo(() => (context.type === 'kpiPivot' ? buildKpisMapper(context.kpis) : {}), [context])
  const cursor = useCursorModel(columns)
  const rows = useRecordsImpl()
  const cursorRow = cursor === undefined ? undefined : rows[cursor.main.rowIndex]
  const cursorColumn = cursor === undefined ? undefined : columns[cursor.main.columnIndex]

  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  const kpi = kpisMapper[value as string]
  if (kpi === undefined) {
    return <></>
  }

  const isContextMenuOpen = cursor?.contextMenuPosition !== undefined
  const helpText = getKpiHelpText(kpi)

  const isContextMenuOpenInCurrentCell = (): boolean =>
    isContextMenuOpen &&
    isCursorOnCurrentColumn(cursorColumn, column) &&
    isCursorOnCurrentRow(cursor, cursorRow, recordNode)

  // kpi, rowKpiなどはcellBody側でpaddingを無視している
  return (
    <Tooltip
      title={helpText}
      placement="right"
      trigger={['hover']}
      open={
        // コンテキストメニューがこのセルで開いている時はツールチップがコンテキストメニューに重なってしまうので
        // 明示的にopen=falseを指定してtooltipを閉じる
        !isContextMenuOpenInCurrentCell() && undefined
      }
    >
      <div className="flex size-full items-center justify-center align-middle">
        <div className="w-full text-center" style={{ lineHeight: 1.3, marginTop: 2 }}>
          <KpiIcon imageName={kpi.icon} style={{ fontSize: 22 }} />
          <br />
          <span className="w-full" style={{ fontSize: 11, whiteSpace: 'normal', wordBreak: 'break-all' }}>
            {kpi.label}
          </span>
        </div>
      </div>
    </Tooltip>
  )
}

const isCursorOnCurrentColumn = (
  cursorColumn?: RSheetColumn<RSheetRecordNode>,
  currentColumn?: RSheetColumn<RSheetRecordNode>,
): boolean => {
  if (cursorColumn === undefined || currentColumn === undefined) {
    return false
  }

  return cursorColumn.index === currentColumn.index
}

const isCursorOnCurrentRow = (
  cursor?: RSheetsCursor,
  cursorRow?: ViewQueryRecordNode,
  currentNode?: ViewQueryRecordNodeWithParent,
): boolean => {
  if (cursor === undefined || cursorRow === undefined || currentNode === undefined) {
    return false
  }

  const parents = getRecordNodeParents(currentNode)
  const rootNode = parents.at(-1)

  return rootNode?.id === cursorRow.id && currentNode.meta.innerRowIndexStart === cursor.main.innerRowIndex
}
